import { IPText } from 'baseComponents';
import { IPChoiceGroup } from 'baseComponents/ChoiceGroup/IPChoiceGroup';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPToggle } from 'baseComponents/Toggle/IPToggle';
import { globalStackTokensGapSmall } from 'globalStyles';
import { TFunction } from 'i18next';
import { DocumentOutputFolderRule, DocumentOutputPurpose } from 'models/documentOutputLocation';
import { FolderNameToken } from 'models/outputLocationBinding';
import { useTranslation } from 'react-i18next';
import FolderNameFormatField from './FolderNameFormatField';
import { getOutputFolderRuleText } from './outputLocationTexts';

interface IEvidenceFolderSettingsProps {
  purpose: DocumentOutputPurpose;
  // A template row: each value gets a toggle to take the tenant's value, and undefined then means that value.
  isTemplate: boolean;
  folderRule: DocumentOutputFolderRule | undefined;
  folderNameFormat: string | undefined;
  // The tenant's values, shown where a template takes them.
  defaultFolderRule: DocumentOutputFolderRule;
  defaultFolderNameFormat: string;
  tokens: FolderNameToken[];
  disabled?: boolean;
  onChange: (folderRule: DocumentOutputFolderRule | undefined, folderNameFormat: string | undefined) => void;
  onProblemsChange?: (problems: string[]) => void;
}

/**
 * Whether the files go into a folder of their own, one per task for the evidence uploads, one per KPI for the KPI
 * attachments and one per asset for the Asset documents, and how that folder is named.
 */
const EvidenceFolderSettings = (props: IEvidenceFolderSettingsProps) => {
  const { t } = useTranslation(['outputLocations']);

  const ruleInherited = props.isTemplate && props.folderRule === undefined;
  const formatInherited = props.isTemplate && props.folderNameFormat === undefined;
  const effectiveRule = props.folderRule ?? props.defaultFolderRule;
  const effectiveFormat = props.folderNameFormat ?? props.defaultFolderNameFormat;
  const folderRule =
    props.purpose === DocumentOutputPurpose.KpiAttachments
      ? DocumentOutputFolderRule.PerKpi
      : props.purpose === DocumentOutputPurpose.AssetDocuments
      ? DocumentOutputFolderRule.PerAsset
      : DocumentOutputFolderRule.PerTask;
  //the name applies to that folder only; it stays in place, disabled, so the modal keeps its height
  const formatApplies = effectiveRule === folderRule;

  return (
    <IPStack tokens={globalStackTokensGapSmall}>
      {props.isTemplate && (
        <IPToggle
          label={t('outputLocations:Evidence.UseDefaultRule')}
          inlineLabel
          checked={ruleInherited}
          disabled={props.disabled}
          onChange={(_ev, checked) =>
            props.onChange(checked ? undefined : props.defaultFolderRule, props.folderNameFormat)
          }
        />
      )}
      {props.isTemplate && (
        <IPText>
          {t('outputLocations:Evidence.TemplateModal.Defaults.Rule', {
            rule: getOutputFolderRuleText(props.defaultFolderRule, t as TFunction<string[]>),
          })}
        </IPText>
      )}
      <IPChoiceGroup
        selectedKey={formatApplies ? 'folder' : 'flat'}
        disabled={props.disabled || ruleInherited}
        options={[
          { key: 'flat', text: t('outputLocations:Evidence.Rule.Flat') },
          {
            key: 'folder',
            text:
              props.purpose === DocumentOutputPurpose.KpiAttachments
                ? t('outputLocations:Kpi.Rule.PerKpi')
                : props.purpose === DocumentOutputPurpose.AssetDocuments
                ? t('outputLocations:Asset.Rule.PerAsset')
                : t('outputLocations:Evidence.Rule.PerTask'),
          },
        ]}
        onChange={(_ev, option) =>
          props.onChange(option?.key === 'folder' ? folderRule : DocumentOutputFolderRule.Flat, props.folderNameFormat)
        }
      />
      {props.isTemplate && (
        <IPToggle
          label={t('outputLocations:Evidence.UseDefaultFormat')}
          inlineLabel
          checked={formatInherited}
          disabled={props.disabled || !formatApplies}
          onChange={(_ev, checked) =>
            props.onChange(props.folderRule, checked ? undefined : props.defaultFolderNameFormat)
          }
        />
      )}
      {props.isTemplate && (
        <IPText>
          {t('outputLocations:Evidence.TemplateModal.Defaults.Format', { format: props.defaultFolderNameFormat })}
        </IPText>
      )}
      <FolderNameFormatField
        purpose={props.purpose}
        value={effectiveFormat}
        tokens={props.tokens}
        disabled={props.disabled || formatInherited || !formatApplies}
        onChange={(value) => props.onChange(props.folderRule, value)}
        onProblemsChange={props.onProblemsChange}
      />
    </IPStack>
  );
};

export default EvidenceFolderSettings;
